'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIsClient, useMediaQuery } from '@monoimin/hooks';

import { slideToActiveParams } from './carousel.types';
import { CarouselDisplay } from './CarouselDisplay';

export interface CarouselProps extends ComponentCommonProps {
  slidesPerPage?: 1 | 2 | 3;
  className?: string;
  wrapperClassName?: string;
  extraContentTop?: any;
  slidesCount: number;
  children: any;
  onSwipeNav?: (newActive: number, container: HTMLDivElement) => void;
  onClickNav?: (
    newActive: number,
    container: HTMLDivElement,
    isPageScroll: boolean
  ) => void;
  title?: string;
}

export const Carousel = ({
  slidesPerPage = 3,
  className,
  wrapperClassName,
  extraContentTop,
  children,
  slidesCount,
  onSwipeNav,
  onClickNav,
  title,
}: CarouselProps) => {
  // определяем тип скролла для аналитики
  const scrollTypeRef = useRef<'swipe' | 'click'>('swipe');
  // сюда кладем номер страницы, на которую скроллим, чтобы потом передать в аналитику
  const pageScrollRef = useRef<number | null>(null);

  const [active, setActive] = useState(0);
  const [max, setMax] = useState(slidesCount);

  const isTablet = useMediaQuery(`(min-width: 640px) and (max-width: 1024px)`);
  const isMobile = useMediaQuery(`(max-width: 640px)`);

  const isClient = useIsClient();

  const isLast = useMemo(() => {
    if (isMobile) {
      return active + 1 === max;
    }
    return active + 1 === max;
  }, [isMobile, active, max]);

  const showNavigation = useMemo(() => {
    if (isMobile) {
      return slidesCount > 1;
    }
    if (isTablet) {
      return slidesCount > 2;
    }

    return slidesCount > 3;
  }, [slidesCount, isTablet, isMobile]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const slideToActive = useCallback(
    (param: slideToActiveParams) => {
      if (!containerRef.current) return;

      scrollTypeRef.current = 'click';

      const container = containerRef.current;
      const scrollStep = container.scrollWidth / slidesCount;
      let scrollTo = container.scrollWidth;
      if (param === 'next') {
        scrollTo = container?.scrollLeft + scrollStep;
      }
      if (param === 'prev') {
        scrollTo = container?.scrollLeft - scrollStep;
      }
      if (typeof param === 'number') {
        scrollTo = scrollStep * param;
        pageScrollRef.current = param;
      }
      container.scrollTo({
        left: scrollTo,
        behavior: 'smooth',
      });
    },
    [containerRef, slidesCount]
  );

  const slideToNext = useCallback(() => slideToActive('next'), []);

  const slideToPrev = useCallback(() => slideToActive('prev'), []);

  const checkMax = useCallback(() => {
    if (isMobile) {
      setMax(slidesCount);
    }
    if (!isTablet && !isMobile) {
      setMax(slidesCount - (slidesPerPage - 1));
    }
    if (isTablet) {
      if (slidesCount < 4) {
        setMax(slidesCount - 1);
      } else {
        setMax(slidesCount - (slidesPerPage - 2));
      }
    }
  }, [slidesPerPage, isTablet, isMobile, slidesCount]);

  useEffect(() => checkMax(), [checkMax]);

  const handleOnScroll = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const scrollStep = Math.floor(container.scrollWidth / slidesCount);
    const newActive = Math.round(container.scrollLeft / scrollStep);
    if (active !== newActive) {
      setActive(newActive);

      if (onSwipeNav && scrollTypeRef.current === 'swipe') {
        onSwipeNav(newActive, containerRef.current);
      } else if (onClickNav && scrollTypeRef.current === 'click') {
        const isPageScroll = pageScrollRef.current !== null;

        if (pageScrollRef.current === newActive) {
          onClickNav(newActive, containerRef.current, isPageScroll);
        } else if (!isPageScroll) {
          onClickNav(newActive, containerRef.current, false);
        }
      }

      setTimeout(() => {
        scrollTypeRef.current = 'swipe'; // по умолчанию скролл всегда swipe
        pageScrollRef.current = null;
      }, 100);
    }
  }, [containerRef, active, slidesCount]);

  return (
    <CarouselDisplay
      title={title}
      wrapperClassName={wrapperClassName}
      extraContentTop={extraContentTop}
      className={className}
      containerRef={containerRef}
      handleOnScroll={handleOnScroll}
      showNavigation={showNavigation}
      isClient={isClient}
      slideToPrev={slideToPrev}
      slideToNext={slideToNext}
      active={active}
      isLast={isLast}
      slideToActive={slideToActive}
      max={max}
    >
      {children}
    </CarouselDisplay>
  );
};
